import React, { useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Collapse, Stack } from "@mui/material";
import { format } from "date-fns";
import { orderBy } from "lodash";

import useRelatedTradeProposals, {
  TradeProposalId,
} from "@fartherfinance/frontend/api/Investment/hooks/useRelatedTradeProposals";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Chip from "@src/sharedComponents/Chip/Chip";
import Select from "@src/sharedComponents/Select/Select";
import SelectItem from "@src/sharedComponents/Select/SelectItem";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import { ErrorRelatedTrades, LoadingRelatedTrades } from "./LoadingErrorTrades";
import RelatedTradesDetails from "./RelatedTradesDetails";
import useCachedRelatedProposals from "./useCachedRelatedProposals";

const defaultSubmittedAtValue = "All";

import styles from "./RelatedTrades.module.css";

type Props = {
  tradeProposalId: TradeProposalId;
};

// format Date and remove duplicates to feed the Dropdown options
const formatDateOptions = (dates: Date[]): string[] => {
  const formattedDates = dates.map((date) => format(date, "MM/dd/yyyy"));
  const removeDuplicates = [...new Set(formattedDates)];
  const orderArray = orderBy(
    removeDuplicates,
    [(date) => new Date(date).getTime()],
    ["desc"]
  );

  return [defaultSubmittedAtValue, ...orderArray];
};

const columns = [
  "Date",
  "Account",
  "Ticker",
  "Side",
  "Quantity",
  "Price",
  "Market Value",
] as const;

const RelatedTrades: React.FC<Props> = ({ tradeProposalId }) => {
  const {
    color: { $border, $surfaceLow },
  } = useTheme();

  const auth = useAdvisorRequestAuth();

  const relatedProposals = useRelatedTradeProposals(
    tradeProposalId,
    { orderBy: "createdAt desc", proposalStatus: ["Submitted"] },
    auth
  );

  const [selectedPastProposalDate, setSelectedPastProposalDate] = useState(
    defaultSubmittedAtValue
  );

  const relatedProposalsData = useCachedRelatedProposals(
    relatedProposals?.data?.data,
    selectedPastProposalDate !== defaultSubmittedAtValue
      ? new Date(selectedPastProposalDate)
      : undefined
  );

  const [isCollapsed, setIsCollapsed] = useState(false);

  if (relatedProposals.isLoading) {
    return <LoadingRelatedTrades />;
  }

  if (relatedProposals.hasError) {
    return <ErrorRelatedTrades />;
  }

  return (
    <Stack className={styles.container}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={8}
        padding={2}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <Stack direction="row" alignItems="center" spacing={8}>
          <Stack direction={"row"} spacing={2}>
            <ChevronRightIcon
              style={{
                transform: isCollapsed ? "rotate(90deg)" : "",
                cursor: relatedProposalsData.isLoading ? "auto" : "pointer",
                opacity: relatedProposalsData.isLoading ? 0.5 : 1,
              }}
            />
            <Text variant="body" style={{ alignContent: "center" }}>
              Last Submitted Proposals
            </Text>
            {relatedProposalsData.isLoading === true ? (
              <Skeleton width={120} />
            ) : (
              <Chip
                variant="outlined"
                label={
                  relatedProposalsData.hasError
                    ? "Failed to load"
                    : relatedProposalsData.data.proposalsLength === 0
                    ? "No Past Proposals"
                    : `${pluralize(
                        relatedProposalsData.data.proposalsLength,
                        "proposal",
                        true
                      )}`
                }
              />
            )}
          </Stack>

          <Stack direction={"row"} spacing={1}>
            {relatedProposalsData?.data?.totalBuys !== undefined ? (
              // pluralize changes 'Buys' to 'Buies', do not use it here
              <Text variant="body">{`${relatedProposalsData.data.totalBuys}
              Buy${
                relatedProposalsData.data.totalBuys === 1 ? "" : "s"
              }`}</Text>
            ) : (
              <Skeleton width={100} />
            )}
            <Text variant="body">{"/"}</Text>
            {relatedProposalsData?.data?.totalSells !== undefined ? (
              <Text variant="body">
                {pluralize(relatedProposalsData.data.totalSells, "Sell", true)}
              </Text>
            ) : (
              <Skeleton width={100} />
            )}
          </Stack>
        </Stack>

        <Stack>
          {relatedProposalsData.isLoading ||
          relatedProposalsData.hasError ||
          relatedProposalsData.data.submittedOn.length === 0 ? (
            <Select
              disabled={true}
              style={{ height: 30, opacity: 0.5 }}
              options={["None"]}
              value={"None"}
              onChange={() => {}}
              renderValue={(value) => value}
              renderOption={(value) => (
                <SelectItem key={value} value={value} label={value} />
              )}
            />
          ) : (
            <Select
              style={{ height: 30 }}
              options={formatDateOptions(relatedProposalsData.data.submittedOn)}
              disabled={relatedProposalsData.data.submittedOn.length === 0}
              value={selectedPastProposalDate}
              onClick={(event) => event.stopPropagation()} // prevent collapse on select click
              onChange={setSelectedPastProposalDate}
              renderValue={(value) => value}
              renderOption={(value) => (
                <SelectItem key={value} value={value} label={value} />
              )}
            />
          )}
        </Stack>
      </Stack>

      <Collapse in={isCollapsed} timeout={400}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          spacing={2}
          padding={2}
          border={`1px solid ${$border}`}
          bgcolor={$surfaceLow}
        >
          {columns.map((header, index) => (
            <Text variant="body" className={styles.textRow} key={index}>
              {header}
            </Text>
          ))}
        </Stack>
        <Stack className={styles.tableContainer}>
          {relatedProposals.data.data.map((tpId) => (
            <RelatedTradesDetails
              key={tpId}
              tradeProposalId={tpId}
              submittedAt={
                selectedPastProposalDate !== "All"
                  ? new Date(selectedPastProposalDate)
                  : undefined
              }
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default RelatedTrades;
